import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { membersAPI, authAPI, zonesAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';

const TABS = [
  { key: 'personal', label: 'Personal Details' },
  { key: 'firm',     label: 'Law Firm / Institution' },
  { key: 'password', label: 'Change Password' },
];

export const FIRM_CATEGORIES = [
  'Association',
  'Civil Society Organization',
  'Company',
  'Financial Institution',
  'Firm',
  'Government',
  'Judge / Magistrate',
  'Judiciary',
  'Member of Parliament',
  'Military',
  'Non-Governmental Organization (NGO)',
  'Office of the Attorney General',
  'Parastatal Organization',
  'Police Force',
  'Political Parties',
  'Prisons Department',
  'Self-Employed / Sole Practitioner',
  'Other',
];

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * Day/Month/Year selects instead of a native date input — on many mobile browsers
 * the native picker only lets you step back one month at a time, so reaching a
 * birth year decades ago means dozens of taps.
 */
export function DobField({ label = 'Date of Birth', name = 'date_of_birth', value, onChange, disabled = false, minAge = 18, maxAge = 100 }) {
  // Local state accumulates each pick independently — the field is only "complete" once
  // all three are set, but a partial pick (e.g. day only) must not be forgotten while
  // the user is still choosing month/year, so this can't be purely derived from `value`.
  const parseValue = (v) => {
    const [yy, mm, dd] = (v || '').split('-').map((n) => parseInt(n, 10));
    return { y: yy || '', m: mm || '', d: dd || '' };
  };
  const [{ y, m, d }, setParts] = useState(() => parseValue(value));

  useEffect(() => {
    setParts(parseValue(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const currentYear = new Date().getFullYear();
  const years = [];
  for (let yr = currentYear - minAge; yr >= currentYear - maxAge; yr--) years.push(yr);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  function handlePick(newD, newM, newY) {
    setParts({ d: newD, m: newM, y: newY });
    if (newD && newM && newY) {
      const mm = String(newM).padStart(2, '0');
      const dd = String(newD).padStart(2, '0');
      onChange({ target: { name, value: `${newY}-${mm}-${dd}` } });
    }
  }

  const selectCls = 'w-full min-w-0 appearance-none rounded-xl border border-slate-200 bg-white pl-2.5 pr-6 py-2.5 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 disabled:bg-slate-50 disabled:text-slate-400';

  const Chevron = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      className="pointer-events-none absolute right-1.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400">
      <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
    </svg>
  );

  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
      <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
        <div className="relative min-w-0">
          <select value={d || ''} onChange={(e) => handlePick(Number(e.target.value), m, y)} disabled={disabled} className={selectCls}>
            <option value="">Day</option>
            {days.map((day) => <option key={day} value={day}>{day}</option>)}
          </select>
          <Chevron />
        </div>
        <div className="relative min-w-0">
          <select value={m || ''} onChange={(e) => handlePick(d, Number(e.target.value), y)} disabled={disabled} className={selectCls}>
            <option value="">Month</option>
            {MONTH_NAMES.map((name, i) => <option key={name} value={i + 1}>{name}</option>)}
          </select>
          <Chevron />
        </div>
        <div className="relative min-w-0">
          <select value={y || ''} onChange={(e) => handlePick(d, m, Number(e.target.value))} disabled={disabled} className={selectCls}>
            <option value="">Year</option>
            {years.map((yr) => <option key={yr} value={yr}>{yr}</option>)}
          </select>
          <Chevron />
        </div>
      </div>
    </div>
  );
}

export default function MemberProfile() {
  const { user } = useAuthStore();
  const location = useLocation();
  const [member, setMember]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [activeTab, setActiveTab] = useState('personal');

  useEffect(() => {
    membersAPI.me()
      .then(setMember)
      .catch(() => setError('Failed to load member profile.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-orange-500 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">{error}</div>
    );
  }

  const displayName = member?.full_name || user?.full_name || 'Member';

  return (
    <div className="space-y-5">

      {location.state?.notice && (
        <div className="notice-blink rounded-2xl border-2 border-red-300 bg-red-50 px-4 py-3 text-sm font-bold text-red-600">
          {location.state.notice}
        </div>
      )}

      {/* ── Header card with picture ── */}
      <ProfileHeader member={member} displayName={displayName} onPictureUpdated={pic => setMember(m => ({ ...m, picture: pic }))} />

      {/* ── Tabs ── */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex border-b border-slate-200">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`flex-1 px-2 py-3 text-xs font-semibold transition-colors sm:px-5 sm:text-sm
                ${activeTab === t.key ? 'border-b-2 border-orange-500 text-orange-600' : 'text-slate-500 hover:text-slate-800'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="p-5">
          {activeTab === 'personal' && <PersonalSection member={member} onSaved={setMember} />}
          {activeTab === 'firm'     && <FirmSection     member={member} onSaved={setMember} />}
          {activeTab === 'password' && <ChangePasswordForm />}
        </div>
      </div>
    </div>
  );
}

// ── Profile Header with picture upload ──────────────────────────────────────

function ProfileHeader({ member, displayName, onPictureUpdated }) {
  const fileRef  = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [picError, setPicError]   = useState('');
  const initial = displayName[0].toUpperCase();
  const pictureUrl = member?.picture ? member.picture : null;

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) { setPicError('Only JPG, PNG or WebP allowed.'); return; }
    if (file.size > 3 * 1024 * 1024)  { setPicError('File must be under 3 MB.'); return; }

    setPicError('');
    setUploading(true);
    try {
      const res = await membersAPI.uploadPicture(file);
      onPictureUpdated(res.picture);
    } catch (err) {
      setPicError(err.response?.data?.error || 'Upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="overflow-hidden rounded-2xl bg-[linear-gradient(135deg,#f07415,#0f172a)] p-6 text-white shadow-lg">
      <div className="flex flex-wrap items-center gap-5">

        {/* Avatar / Picture */}
        <div className="relative shrink-0">
          <div className="h-20 w-20 rounded-2xl overflow-hidden bg-white/20 backdrop-blur-sm flex items-center justify-center">
            {pictureUrl ? (
              <img
                src={pictureUrl}
                alt={displayName}
                className="h-full w-full object-cover"
                onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
            ) : null}
            <span className={`text-2xl font-black text-white ${pictureUrl ? 'hidden' : 'flex'} items-center justify-center h-full w-full`}>
              {initial}
            </span>
          </div>

          {/* Camera button */}
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            title="Change profile picture"
            className="absolute -bottom-1.5 -right-1.5 h-7 w-7 rounded-full bg-orange-500 hover:bg-orange-400 border-2 border-white flex items-center justify-center shadow-md transition-colors disabled:opacity-60">
            {uploading ? (
              <svg className="h-3 w-3 animate-spin text-white" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
              </svg>
            ) : (
              <svg className="h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
            )}
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFileChange} />
        </div>

        {/* Name + badges */}
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold truncate">{displayName}</h1>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {member?.roll_no ? (
              <span className="rounded-full bg-white/20 px-3 py-0.5 text-xs font-semibold">{member.roll_no}</span>
            ) : member?.petition_no ? (
              <span className="rounded-full bg-white/20 px-3 py-0.5 text-xs font-semibold">Petition #{member.petition_no}</span>
            ) : null}
            <span className={`rounded-full px-3 py-0.5 text-xs font-semibold
              ${member?.status === 'active' ? 'bg-emerald-400/30 text-emerald-100' : 'bg-white/10 text-white/70'}`}>
              {member?.status === 'active' ? 'Active Member' : (member?.status || 'Member')}
            </span>
            {member?.membership_type === 'life' && (
              <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-300 to-amber-500 px-3 py-0.5 text-xs font-bold text-amber-950">
                🏆 Life Member
              </span>
            )}
          </div>
          {member?.membership_type === 'life' && (
            <p className="mt-2 text-xs font-semibold text-amber-200">
              🎉 Congratulations on becoming an AWTF Life Member{member.life_member_date ? ` since ${member.life_member_date}` : ''}!
            </p>
          )}
          {picError && (
            <p className="mt-2 text-xs text-red-300">{picError}</p>
          )}
          {!picError && (
            <p className="mt-2 text-[11px] text-white/50">
              {uploading ? 'Uploading…' : 'Click the camera icon to change your profile picture'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Personal Section ─────────────────────────────────────────────────────────

function InfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
      <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">{label}</span>
      <span className="ml-4 text-right text-sm font-semibold text-slate-800">{value || '—'}</span>
    </div>
  );
}

const GENDER_OPTIONS = ['Male', 'Female'];

function PersonalSection({ member, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [zones, setZones]     = useState([]);
  const [form, setForm] = useState({
    phone:         member?.phone         || '',
    email:         member?.email         || '',
    gender:        member?.gender        || '',
    date_of_birth: member?.date_of_birth || '',
    chapter:       member?.chapter       || '',
  });

  useEffect(() => {
    zonesAPI.list().then(({ zones }) => setZones(zones)).catch(() => {});
  }, []);

  const zoneForChapter = (chapterName) =>
    zones.find((z) => z.chapters.some((c) => c.name === chapterName));

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleEdit = () => {
    setForm({
      phone:         member?.phone         || '',
      email:         member?.email         || '',
      gender:        member?.gender        || '',
      date_of_birth: member?.date_of_birth || '',
      chapter:       member?.chapter       || '',
    });
    setError(''); setSuccess(''); setEditing(true);
  };

  const selectedZone = zoneForChapter(form.chapter);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      await membersAPI.update(member.id, form);
      onSaved((prev) => ({ ...prev, ...form }));
      setSuccess('Details updated successfully.');
      setEditing(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save changes.');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      {error   && <div className="rounded-xl border border-red-200   bg-red-50   px-4 py-2.5 text-sm text-red-700">{error}</div>}
      {success && <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-700">{success}</div>}
      <InfoRow label="Full Name" value={member?.full_name} />
      {editing ? (
        <form onSubmit={handleSave} className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FirmField label="Phone"         name="phone"         value={form.phone}         onChange={handleChange} />
            <FirmField label="Email"         name="email"         value={form.email}         onChange={handleChange} type="email" />
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Gender</label>
              <select name="gender" value={form.gender} onChange={handleChange}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20">
                <option value="">Select gender</option>
                {GENDER_OPTIONS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <DobField value={form.date_of_birth} onChange={handleChange} />
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Chapter</label>
              <select name="chapter" value={form.chapter} onChange={handleChange}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20">
                <option value="">Select chapter</option>
                {zones.map((z) => (
                  <optgroup key={z.id} label={z.name}>
                    {z.chapters.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </optgroup>
                ))}
              </select>
              {selectedZone && (
                <p className="mt-1.5 text-xs font-medium text-orange-600">Zone: {selectedZone.name}</p>
              )}
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button type="submit" disabled={saving}
              className="rounded-xl bg-slate-800 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-900 disabled:opacity-60">
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button type="button" onClick={() => setEditing(false)}
              className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          <div className="mb-3 flex justify-end">
            <button onClick={handleEdit}
              className="rounded-xl border border-orange-300 bg-orange-50 px-4 py-2 text-xs font-semibold text-orange-700 shadow-sm hover:bg-orange-100">
              ✏ Edit Details
            </button>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <InfoRow label="Phone"         value={member?.phone} />
            <InfoRow label="Email"         value={member?.email} />
            <InfoRow label="Gender"        value={member?.gender} />
            <InfoRow label="Date of Birth" value={member?.date_of_birth} />
            <InfoRow label="Zone"          value={zoneForChapter(member?.chapter)?.name} />
            <InfoRow label="Chapter"       value={member?.chapter} />
            <InfoRow label="Admission Date" value={member?.admission_date} />
          </div>
          {member?.address && (
            <div className="mt-2 rounded-xl bg-slate-50 px-4 py-3">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Address</span>
              <p className="mt-1 text-sm font-semibold text-slate-800">{member.address}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Firm Section ─────────────────────────────────────────────────────────────

function FirmSection({ member, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    firm_name:     member?.firm_name     || '',
    firm_location: member?.firm_location || '',
    firm_tin:      member?.firm_tin      || '',
    firm_address:  member?.firm_address  || '',
    firm_category: member?.firm_category || '',
  });

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleEdit = () => {
    setForm({
      firm_name:     member?.firm_name     || '',
      firm_location: member?.firm_location || '',
      firm_tin:      member?.firm_tin      || '',
      firm_address:  member?.firm_address  || '',
      firm_category: member?.firm_category || '',
    });
    setError(''); setSuccess(''); setEditing(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      await membersAPI.update(member.id, form);
      onSaved((prev) => ({ ...prev, ...form }));
      setSuccess('Firm details updated successfully.');
      setEditing(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save changes.');
    } finally { setSaving(false); }
  };

  const hasFirmData = member?.firm_name || member?.firm_location || member?.firm_address || member?.firm_tin || member?.firm_category;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Law Firm / Institution</p>
        {!editing && (
          <button onClick={handleEdit}
            className="rounded-lg border border-orange-300 bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-700 shadow-sm hover:bg-orange-100">
            ✏ Edit
          </button>
        )}
      </div>
      {error   && <div className="mb-3 rounded-xl border border-red-200   bg-red-50   px-4 py-2.5 text-sm text-red-700">{error}</div>}
      {success && <div className="mb-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-700">{success}</div>}
      {editing ? (
        <form onSubmit={handleSave} className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FirmField label="Firm / Institution Name" name="firm_name"     value={form.firm_name}     onChange={handleChange} />
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Category</label>
              <select name="firm_category" value={form.firm_category} onChange={handleChange}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20">
                <option value="">Select category…</option>
                {FIRM_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <FirmField label="Location"                name="firm_location" value={form.firm_location} onChange={handleChange} />
            <FirmField label="TIN Number"              name="firm_tin"      value={form.firm_tin}      onChange={handleChange} />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Address</label>
            <textarea name="firm_address" value={form.firm_address} onChange={handleChange} rows={2}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20" />
          </div>
          <div className="flex gap-2 pt-1">
            <button type="submit" disabled={saving}
              className="rounded-xl bg-slate-800 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-900 disabled:opacity-60">
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button type="button" onClick={() => setEditing(false)}
              className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
              Cancel
            </button>
          </div>
        </form>
      ) : hasFirmData ? (
        <>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <InfoRow label="Name"     value={member?.firm_name} />
            <InfoRow label="Category" value={member?.firm_category} />
            <InfoRow label="Location" value={member?.firm_location} />
            <InfoRow label="TIN"      value={member?.firm_tin} />
          </div>
          {member?.firm_address && (
            <div className="mt-2 rounded-xl bg-slate-50 px-4 py-3">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Address</span>
              <p className="mt-1 text-sm font-semibold text-slate-800">{member.firm_address}</p>
            </div>
          )}
        </>
      ) : (
        <p className="text-sm text-slate-400">No firm or institution details added yet. Click Edit to add.</p>
      )}
    </div>
  );
}

// ── Change Password ──────────────────────────────────────────────────────────

function ChangePasswordForm() {
  const [form, setForm]       = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving]   = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError]     = useState('');

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSuccess('');
    if (form.new_password !== form.confirm_password) { setError('New passwords do not match.'); return; }
    if (form.new_password.length < 6)                { setError('New password must be at least 6 characters.'); return; }
    setSaving(true);
    try {
      await authAPI.changePassword({ old_password: form.current_password, new_password: form.new_password });
      setSuccess('Password changed successfully.');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password.');
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="mb-4 text-xs font-bold uppercase tracking-widest text-slate-400">Change Password</p>
      {error   && <div className="mb-4 rounded-xl border border-red-200   bg-red-50   px-4 py-3 text-sm text-red-700">{error}</div>}
      {success && <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{success}</div>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <PasswordField label="Current Password"  name="current_password" value={form.current_password} onChange={handleChange} />
        <div className="grid gap-4 sm:grid-cols-2">
          <PasswordField label="New Password"     name="new_password"     value={form.new_password}     onChange={handleChange} />
          <PasswordField label="Confirm Password" name="confirm_password" value={form.confirm_password} onChange={handleChange} />
        </div>
        <button type="submit" disabled={saving}
          className="rounded-xl bg-slate-800 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-900 disabled:opacity-60">
          {saving ? 'Saving...' : 'Update Password'}
        </button>
      </form>
    </div>
  );
}

// ── Shared field components ──────────────────────────────────────────────────

function FirmField({ label, name, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
      <input type={type} name={name} value={value} onChange={onChange}
        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20" />
    </div>
  );
}

function PasswordField({ label, name, value, onChange }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
      <input type="password" name={name} value={value} onChange={onChange} required
        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20" />
    </div>
  );
}
